import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, Calendar, BookOpen } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  meta_description: string | null;
  hero_image_url: string | null;
  hero_image_alt: string | null;
  published_at: string | null;
  reading_time: number | null;
  language: string | null;
}

const PAGE_SIZE = 12;

const LOCALES: Record<string, string> = {
  de: "de-DE",
  en: "en-US",
  fr: "fr-FR",
  nl: "nl-NL",
  it: "it-IT",
  es: "es-ES",
};

const T = {
  de: {
    title: "Blog | EAP Abutments",
    heading: "Blog",
    intro: "Einblicke, Forschung und Updates zur dentalen Implantologie und EAP®-Technologie.",
    metaDesc: "Aktuelle Artikel und Einblicke rund um dentale Implantologie, Abutments und biokompatible Lösungen.",
    minRead: "Min. Lesezeit",
    loadMore: "Mehr laden",
    loading: "Lädt…",
    retry: "Erneut versuchen",
    error: "Artikel konnten nicht geladen werden. Bitte erneut versuchen.",
    emptyTitle: "Artikel sind in Vorbereitung!",
    emptyText: "Schau bald wieder vorbei.",
  },
  en: {
    title: "Blog | EAP Abutments",
    heading: "Blog",
    intro: "Insights, research, and updates on dental implantology and EAP® technology.",
    metaDesc: "Read our latest articles and insights on dental implantology, abutments, and biocompatible solutions.",
    minRead: "min read",
    loadMore: "Load more",
    loading: "Loading…",
    retry: "Retry",
    error: "Couldn't load articles. Please try again.",
    emptyTitle: "Articles are on the way!",
    emptyText: "Check back soon.",
  },
  fr: {
    title: "Blog | EAP Abutments",
    heading: "Blog",
    intro: "Analyses, recherche et actualités sur l'implantologie dentaire et la technologie EAP®.",
    metaDesc: "Découvrez nos derniers articles sur l'implantologie dentaire, les piliers et les solutions biocompatibles.",
    minRead: "min de lecture",
    loadMore: "Charger plus",
    loading: "Chargement…",
    retry: "Réessayer",
    error: "Impossible de charger les articles. Veuillez réessayer.",
    emptyTitle: "Les articles arrivent !",
    emptyText: "Revenez bientôt.",
  },
  nl: {
    title: "Blog | EAP Abutments",
    heading: "Blog",
    intro: "Inzichten, onderzoek en updates over dentale implantologie en EAP®-technologie.",
    metaDesc: "Lees onze recente artikelen over dentale implantologie, abutments en biocompatibele oplossingen.",
    minRead: "min leestijd",
    loadMore: "Meer laden",
    loading: "Laden…",
    retry: "Opnieuw proberen",
    error: "Artikelen konden niet worden geladen. Probeer het opnieuw.",
    emptyTitle: "Artikelen zijn onderweg!",
    emptyText: "Kom snel terug.",
  },
  it: {
    title: "Blog | EAP Abutments",
    heading: "Blog",
    intro: "Approfondimenti, ricerca e aggiornamenti sull'implantologia dentale e la tecnologia EAP®.",
    metaDesc: "Leggi i nostri articoli più recenti su implantologia dentale, abutment e soluzioni biocompatibili.",
    minRead: "min di lettura",
    loadMore: "Carica altro",
    loading: "Caricamento…",
    retry: "Riprova",
    error: "Impossibile caricare gli articoli. Riprova.",
    emptyTitle: "Gli articoli sono in arrivo!",
    emptyText: "Torna presto.",
  },
  es: {
    title: "Blog | EAP Abutments",
    heading: "Blog",
    intro: "Análisis, investigación y novedades sobre implantología dental y la tecnología EAP®.",
    metaDesc: "Lee nuestros últimos artículos sobre implantología dental, pilares y soluciones biocompatibles.",
    minRead: "min de lectura",
    loadMore: "Cargar más",
    loading: "Cargando…",
    retry: "Reintentar",
    error: "No se pudieron cargar los artículos. Inténtalo de nuevo.",
    emptyTitle: "¡Los artículos están en camino!",
    emptyText: "Vuelve pronto.",
  },
} as const;

export const formatPostDate = (iso: string | null, lang: string) => {
  if (!iso) return "";
  const locale = LOCALES[lang] || "en-US";
  return new Date(iso).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const truncate = (text: string | null, max = 160) => {
  if (!text) return "";
  return text.length > max ? text.slice(0, max).trimEnd() + "…" : text;
};

const Blog = () => {
  const { language } = useLanguage();
  const t = T[language];

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const fetchPosts = async (pageToLoad: number, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    const from = pageToLoad * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    // Match e.g. "de" to "de", "de-AT", "de-CH"; fall back to all if empty
    let query = supabase
      .from("blog_posts")
      .select(
        "id, title, slug, meta_description, hero_image_url, hero_image_alt, published_at, reading_time, language"
      )
      .or(`language.eq.${language},language.ilike.${language}-%`)
      .order("published_at", { ascending: false, nullsFirst: false })
      .range(from, to);

    let { data, error: err } = await query;

    // Fallback: if no posts in the current language at all, show all
    if (!err && pageToLoad === 0 && (!data || data.length === 0)) {
      const all = await supabase
        .from("blog_posts")
        .select(
          "id, title, slug, meta_description, hero_image_url, hero_image_alt, published_at, reading_time, language"
        )
        .order("published_at", { ascending: false, nullsFirst: false })
        .range(from, to);
      data = all.data;
      err = all.error;
    }

    if (err) {
      setError(err.message);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const newPosts = (data ?? []) as BlogPost[];
    setPosts((prev) => (append ? [...prev, ...newPosts] : newPosts));
    setHasMore(newPosts.length === PAGE_SIZE);
    setLoading(false);
    setLoadingMore(false);
  };

  useEffect(() => {
    setPage(0);
    fetchPosts(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPosts(next, true);
  };

  const handleRetry = () => {
    setPage(0);
    fetchPosts(0, false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{t.title}</title>
        <meta name="description" content={t.metaDesc} />
        <link rel="canonical" href="https://eap-abutments.com/blog" />
        <meta property="og:title" content={t.title} />
        <meta property="og:description" content={t.metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://eap-abutments.com/blog" />
      </Helmet>

      <Navigation />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <header className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">{t.heading}</h1>
            <p className="text-lg text-muted-foreground">{t.intro}</p>
          </header>

          {loading && (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="h-48 w-full" />
                  <CardContent className="p-6 space-y-3">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-1/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="max-w-md mx-auto text-center py-16">
              <p className="text-foreground mb-4">{t.error}</p>
              <Button onClick={handleRetry}>{t.retry}</Button>
            </div>
          )}

          {!loading && !error && posts.length === 0 && (
            <div className="max-w-md mx-auto text-center py-16">
              <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">{t.emptyTitle}</h2>
              <p className="text-muted-foreground">{t.emptyText}</p>
            </div>
          )}

          {!loading && !error && posts.length > 0 && (
            <>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {posts.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="group cursor-pointer">
                    <Card className="overflow-hidden h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                      {post.hero_image_url && (
                        <div className="aspect-video w-full overflow-hidden bg-muted">
                          <img
                            src={post.hero_image_url}
                            alt={post.hero_image_alt || post.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => {
                              (e.currentTarget.parentElement as HTMLElement).style.display = "none";
                            }}
                          />
                        </div>
                      )}
                      <CardContent className="p-6">
                        <h2 className="text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        {post.meta_description && (
                          <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                            {truncate(post.meta_description, 160)}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          {post.published_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatPostDate(post.published_at, language)}
                            </span>
                          )}
                          {post.reading_time && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.reading_time} {t.minRead}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>

              {hasMore && (
                <div className="text-center mt-12">
                  <Button onClick={handleLoadMore} disabled={loadingMore} size="lg">
                    {loadingMore ? t.loading : t.loadMore}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
